<template>
  <div>
    <div class="tw:pb-0" v-if="!loading">
      <AuthMenu isLanding @onRegisterClick="onAuthorize('register')" />
      <div>
        <LandingPageHero
          v-scroll-animate="{ duration: '2s', delay: '0.3s' }"
          class="element hidden-start"
          @onLoginClick="onAuthorize('login')"
        />
      </div>
      <LandingPageSocialProof v-scroll-animate="{ duration: '2s', delay: '0.3s' }" class="tw:bg-white" />
      <LandingPageFreeBusinessAccount v-scroll-animate="{ duration: '2s', delay: '0.3s' }" />
      <LandingPageInfoBanner v-scroll-animate="{ duration: '2s', delay: '0.3s' }" class="tw:bg-white" />
      <LandingPageRateConnect v-scroll-animate="{ duration: '2s', delay: '0.3s' }" />
      <LandingPageStudioPlus v-scroll-animate="{ duration: '2s', delay: '0.3s' }" class="tw:bg-white" />
      <LandingPageProductsComparison
        v-scroll-animate="{ duration: '2s', delay: '0.3s' }"
        title="tbs_mp_header_premium"
        product="both"
        @onLoginClick="onAuthorize('login')"
      />
      <ProductPageFaq
        v-scroll-animate="{ duration: '2s', delay: '0.3s' }"
        id="landing-page-products"
        class="tw:bg-white"
        pageName="landing"
      />
    </div>
    <div v-else>
      <LaunchpadLoader class="tw:h-screen tw:flex tw:items-center tw:justify-center" />
    </div>
    <ThePageFooter isLanding />
    <ChatWidget />
  </div>
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router'
import { computed, onMounted, ref, type ComputedRef } from 'vue'
import { useStore } from 'vuex'

import AuthMenu from '@/components/landingPage/AuthMenu.vue'
import LandingPageHero from '@/components/landingPage/LandingPageHero.vue'
import LandingPageSocialProof from '@/components/landingPage/LandingPageSocialProof.vue'
import LandingPageFreeBusinessAccount from '@/components/landingPage/LandingPageFreeBusinessAccount.vue'
import LandingPageInfoBanner from '@/components/landingPage/LandingPageInfoBanner.vue'
import LandingPageRateConnect from '@/components/landingPage/LandingPageRateConnect.vue'
import LandingPageStudioPlus from '@/components/landingPage/LandingPageStudioPlus.vue'
import LandingPageProductsComparison from '@/components/landingPage/LandingPageProductsComparison.vue'
import LaunchpadLoader from '@/components/dashboard/common/LaunchpadLoader.vue'
import ProductPageFaq from '@/components/productPage/ProductPageFaq.vue'
import ThePageFooter from '@/components/page/ThePageFooter.vue'
import ChatWidget from '@/components/chatbot/ChatWidget.vue'

import appAnalytics from '@/utils/tracking'
import type { AuthType, AuthData, Locale } from '@/types'

// ==================== Component Setup ====================

const route = useRoute()
const store = useStore()

/** Current locale from store */
const locale: ComputedRef<Locale> = computed(() => store.state.locale)

/** Loading state (true while auto-login is in progress) */
const loading = ref<boolean>(true)

// ==================== Functions ====================

/**
 * Dispatch authorization action to store
 * @param params - Authorization parameters
 */
const authorize = (params: AuthData): Promise<void> => store.dispatch('session/authorize', params)

/**
 * Handle authorization (login or signup)
 * Auto-triggers if user came from a redirect
 * @param type - Auth type (login or signup)
 */
const onAuthorize = async (type: AuthType = 'login'): Promise<void> => {
  appAnalytics.track(appAnalytics.events.BUTTON_CLICKED, {
    button: type,
    property_id: null,
  })

  await authorize({
    type,
    stateParams: { ...route.query },
    locale: locale.value.language,
  })
}

// ==================== Lifecycle ====================

/**
 * On mount, check if user should be auto-redirected to login
 */
onMounted(() => {
  if (route.query?.['redirectFrom'] !== undefined) {
    onAuthorize('login')
  } else {
    loading.value = false
  }
})
</script>
